import React, { useRef, useState } from "react";
import api, { resolveImageUrl } from "@/lib/api";
import { Upload, X, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

export default function ImageUploader({ value, onChange, label = "Image", testId = "image-uploader" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState("");

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const clear = () => onChange("");

  return (
    <div className="space-y-2" data-testid={testId}>
      <label className="text-xs font-semibold uppercase tracking-wider text-msr-slate">{label}</label>
      <div className="border-2 border-dashed border-msr-line bg-white p-3 flex items-center gap-3">
        <div className="h-20 w-20 bg-msr-mist border border-msr-line grid place-items-center overflow-hidden">
          {value ? (
            <img src={resolveImageUrl(value)} alt="" className="h-full w-full object-cover" />
          ) : (
            <ImageIcon size={20} className="text-msr-slate" />
          )}
        </div>
        <div className="flex-1 space-y-2">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="inline-flex items-center gap-1.5 bg-msr-blue text-white text-xs font-semibold px-3 py-2 hover:bg-msr-blue-dark disabled:opacity-50"
              data-testid={`${testId}-upload-btn`}
            >
              <Upload size={12} /> {uploading ? "Uploading..." : "Upload"}
            </button>
            {value && (
              <button
                type="button"
                onClick={clear}
                className="inline-flex items-center gap-1.5 bg-white text-msr-ink text-xs font-semibold px-3 py-2 border border-msr-line hover:bg-msr-mist"
                data-testid={`${testId}-clear-btn`}
              >
                <X size={12} /> Remove
              </button>
            )}
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => upload(e.target.files?.[0])}
            />
          </div>
          <div className="flex gap-1.5">
            <input
              type="text"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              placeholder="...or paste an image URL"
              className="flex-1 text-xs border border-msr-line px-2 py-1.5 focus:outline-none focus:border-msr-blue"
              data-testid={`${testId}-url-input`}
            />
            <button
              type="button"
              onClick={() => { if (urlInput) { onChange(urlInput); setUrlInput(""); toast.success("Image URL set"); } }}
              className="text-xs bg-msr-ink text-white px-3 py-1.5 hover:bg-black"
              data-testid={`${testId}-url-save-btn`}
            >
              Use URL
            </button>
          </div>
          {value && (
            <div className="text-[11px] text-msr-slate truncate">{value}</div>
          )}
        </div>
      </div>
    </div>
  );
}
